
import { useEffect, useRef } from 'react';
import { getTodayString, getYesterdayString, getNextGameTime } from '@/lib/utils/date-utils';
import { extractSpreadsheetId, fetchSpreadsheetAsCSV, findColumnIndices, findRowByDate } from "@/utils/sheetUtils";
import { processRowData, createPuzzleData } from "@/utils/puzzleDataUtils";
import { storePuzzleInDB } from "@/lib/database/puzzle-storage";

/**
 * Hook to check if the game should be refreshed (e.g., at midnight)
 * and to pre-fetch the next day's puzzle shortly after midnight
 */
export function useScheduledGameClose() {
  const intervalRef = useRef<number | null>(null);
  const midnightTimerRef = useRef<number | null>(null);
  
  useEffect(() => {
    // Function to update countdown and perform checks
    const updateCountdown = () => {
      const now = new Date();
      const nextGameTime = getNextGameTime();
      
      // Calculate time difference
      const diff = nextGameTime.getTime() - now.getTime();
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      
      // Log countdown info for debugging
      console.info('Scheduled game close countdown:', {
        now: now.toISOString(),
        nextGame: nextGameTime.toISOString(),
        diff,
        hours,
        minutes
      });
      
      // If we're close to midnight (within 5 minutes), check more frequently
      if (diff < 5 * 60 * 1000) {
        // Clear regular interval and set up a more frequent check
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = window.setInterval(updateCountdown, 10000); // Check every 10 seconds
        }
      }
      
      // If midnight has passed, refresh the game
      if (diff <= 0) {
        console.log('Game reset time reached - reloading page');
        // Force reload for the cleanest state
        window.location.reload();
      }
    };
    
    // Set up the initial interval
    if (!intervalRef.current) {
      intervalRef.current = window.setInterval(updateCountdown, 60000); // Check every minute
    }
    
    // Run once immediately
    updateCountdown();
    
    // Check if yesterday's game was properly closed
    const checkYesterdaysGame = async () => {
      const today = getTodayString();
      const yesterday = getYesterdayString();
      
      console.log('Checking if yesterday\'s game needs to be closed:', yesterday);
      
      // Additional logic for auto-fetching new day's puzzle
      try {
        // Schedule a puzzle pre-fetch for shortly after midnight
        const setupMidnightFetch = () => {
          const nextGameTime = getNextGameTime();
          const now = new Date();
          const timeToMidnight = nextGameTime.getTime() - now.getTime();
          
          // Set a delay for 1 minute after midnight to fetch the puzzle
          if (timeToMidnight > 0) {
            const fetchDelay = timeToMidnight + 60000; // 1 minute after midnight
            
            console.log(`Scheduling puzzle pre-fetch for ${new Date(now.getTime() + fetchDelay).toLocaleString()}`);
            
            // Clear any existing timer
            if (midnightTimerRef.current) {
              window.clearTimeout(midnightTimerRef.current);
            }
            
            // Set the new timer
            midnightTimerRef.current = window.setTimeout(() => {
              prefetchTodaysPuzzle();
            }, fetchDelay);
          }
        };
        
        // Function to prefetch the puzzle
        const prefetchTodaysPuzzle = async () => {
          console.log('Auto-fetching new day\'s puzzle after midnight');
          
          try {
            // Get spreadsheet URL from localStorage
            const spreadsheetUrl = localStorage.getItem("external_puzzle_url");
            if (!spreadsheetUrl) return;
            
            const spreadsheetId = extractSpreadsheetId(spreadsheetUrl);
            const newToday = getTodayString(); // Get fresh today string
            
            // Fetch spreadsheet data as CSV
            const rows = await fetchSpreadsheetAsCSV(spreadsheetId);
            
            if (!rows || rows.length < 2) {
              throw new Error("Empty or invalid puzzle data");
            }
            
            // Find column indices from headers
            const { dateIndex, wordIndex, ruleIndex, reasoningIndex, spectrumIndex } = findColumnIndices(rows[0]);
            
            // Find today's row
            const todayRow = findRowByDate(rows, dateIndex, newToday);
            
            if (!todayRow) {
              console.log(`No puzzle found for ${newToday} yet`);
              return;
            }
            
            // Process the row data
            const { words, ruleName, reasoning, spectrum } = processRowData(
              todayRow, 
              wordIndex, 
              ruleIndex, 
              reasoningIndex, 
              spectrumIndex
            );
            
            // Create the puzzle directly from spreadsheet data
            const puzzle = createPuzzleData(words, ruleName, reasoning, spectrum);
            
            // Explicitly set the date to ensure consistency
            puzzle.date = newToday;
            
            // Store the new puzzle in database for all users
            await storePuzzleInDB(puzzle);
            
            console.log('Successfully pre-fetched and stored puzzle for new day:', newToday);
          } catch (error) {
            console.error('Error pre-fetching puzzle:', error);
          }
        };
        
        // Set up the midnight fetch
        setupMidnightFetch();
        
        console.log('Successfully ran game finalization check');
      } catch (error) {
        console.error('Error in game finalization check:', error);
      }
    };
    
    // Run the check
    checkYesterdaysGame();
    
    // Clean up on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (midnightTimerRef.current) {
        clearTimeout(midnightTimerRef.current);
      }
    };
  }, []);
}
